import { Injectable } from '@angular/core';
import { Invoice } from '../invoice.model';
import { Constants, ContractServiceType } from '../../../constants.new';
import { CurrencyPipe, DecimalPipe, PercentPipe } from '@angular/common';
import { Decimal } from 'decimal.js';
import { BehaviorSubject, Observable } from 'rxjs';

export enum OverrideStatus {
    None = 0,
    Manual = 1,
    Auto = 2,
}

export interface IncludeParcelsReset {
    previousTAV?: number;
    previousMV?: number;
    invoiceTAV?: number;
    invoiceMV?: number;
}

export interface FormattedInvoiceDetails {
    previousTAV?: number;
    previousTAVValue?: number;
    previousMV?: number;
    invoiceTAV?: number;
    invoiceTAVValue?: number;
    invoiceMV?: number;
    siteChangeTAV?: number;
    siteChangeMV?: number;
    billableTaxRate?: number;
    taxSavingsTotal?: number;

    contingencyPct?: number;
    contingencyCap?: number;
    feeAmount?: number;
    fixedFee?: number;
    expenseAmount?: number;
    invoiceAmount?: number;

    includeOtherParcels?: boolean;
}

export interface InvoiceDetailOverrides {
    previousTAV: OverrideStatus;
    previousMV: OverrideStatus;
    invoiceTAV: OverrideStatus;
    invoiceMV: OverrideStatus;
    siteChangeTAV: OverrideStatus;
    siteChangeMV: OverrideStatus;
    billableTaxRate: OverrideStatus;
    taxSavingsTotal: OverrideStatus;

    contingencyPct: OverrideStatus;
    contingencyCap: OverrideStatus;
    feeAmount: OverrideStatus;
    fixedFee: OverrideStatus;
    expenseAmount: OverrideStatus;
    invoiceAmount: OverrideStatus;
}

export const SEE_DETAILS = 'See Details...';

@Injectable()
export class InvoiceDetailsService {
    constructor(
        private readonly _numberPipe: DecimalPipe,
        private readonly _currencyPipe: CurrencyPipe,
        private readonly _percentPipe: PercentPipe,
        private readonly _constants: Constants,
    ) {}

    private _currentInvoice: Invoice;

    private _notesSubject = new BehaviorSubject<string>(null);
    private _notes$ = this._notesSubject.asObservable();

    private _originalInvoiceValues: FormattedInvoiceDetails;
    private _invoiceResetValues: FormattedInvoiceDetails;
    private _otherParcelResetValues: FormattedInvoiceDetails;
    private _hasMultipleTaxableValues: boolean = false;
    private _isMultiParcelInvoice: boolean = false;
    private _invoiceInitialized: boolean = false;
    private _hasAnyTaxableAssessedValue: boolean = false;
    private _hasMultipleBaseRates: boolean = false;

    private _overrides: InvoiceDetailOverrides = {
        previousTAV: OverrideStatus.None,
        previousMV: OverrideStatus.None,
        invoiceTAV: OverrideStatus.None,
        invoiceMV: OverrideStatus.None,
        siteChangeTAV: OverrideStatus.None,
        siteChangeMV: OverrideStatus.None,
        billableTaxRate: OverrideStatus.None,
        taxSavingsTotal: OverrideStatus.None,
        contingencyPct: OverrideStatus.None,
        contingencyCap: OverrideStatus.None,
        feeAmount: OverrideStatus.None,
        fixedFee: OverrideStatus.None,
        expenseAmount: OverrideStatus.None,
        invoiceAmount: OverrideStatus.None,
    };

    get notes$(): Observable<string> {
        return this._notes$;
    }

    get overrides(): InvoiceDetailOverrides {
        return this._overrides;
    }

    get originalInvoiceValues(): FormattedInvoiceDetails {
        return this._originalInvoiceValues;
    }

    get hasAnyTaxableAssessedValue(): boolean {
        return this._hasAnyTaxableAssessedValue;
    }

    get invoiceResetValues(): FormattedInvoiceDetails {
        return this._invoiceResetValues;
    }

    get invoiceInitialized(): boolean {
        return this._invoiceInitialized;
    }

    set invoiceInitialized(value: boolean) {
        this._invoiceInitialized = value;
    }

    get hasMultipleBaseRates(): boolean {
        return this._hasMultipleBaseRates;
    }

    processInvoiceChanges(invoice: Invoice): FormattedInvoiceDetails {
        this._currentInvoice = invoice;

        this._hasMultipleTaxableValues = invoice.taxSavings.some((x) =>
            x.parcelSavingsCalculations.some(
                (y) => y.appealRevisionCalculations.length > 1 || y.outcomeRevisionCalculations.length > 1,
            ),
        );

        this._hasAnyTaxableAssessedValue = invoice.taxSavings?.some((x) => x.hasAnyTaxableAssessedValue);

        this._isMultiParcelInvoice = invoice.taxSavings?.length > 1;

        const allBaseRates = invoice.taxSavings?.flatMap((x) => [
            ...x.parcelSavingsCalculations.flatMap((y) => [
                ...y.appealRevisionCalculations.map((z) => z.baseRate),
                ...y.outcomeRevisionCalculations.map((z) => z.baseRate),
            ]),
        ]);

        this._hasMultipleBaseRates = allBaseRates.length > 0 && new Set(allBaseRates).size > 1;

        const invoiceValues = {
            previousTAV: 0,
            previousMV: 0,
            invoiceTAV: 0,
            invoiceMV: 0,
            siteChangeTAV: 0,
            siteChangeMV: 0,
            billableTaxRate: 0,
            taxSavingsTotal: 0,
            contingencyPct: 0,
            contingencyCap: 0,
            feeAmount: 0,
            fixedFee: 0,
            expenseAmount: 0,
            invoiceAmount: 0,
        };
        Object.keys(this._overrides).forEach((key: string) => {
            this._overrides[key] = OverrideStatus.None;
        });

        this._otherParcelResetValues = {
            previousTAV: this._hasAnyTaxableAssessedValue ? (invoice.previousTAVOriginal ?? 0) : null,
            previousMV: invoice.previousMVOriginal ?? 0,
            invoiceTAV: this._hasAnyTaxableAssessedValue ? (invoice.invoiceTAVOriginal ?? 0) : null,
            invoiceMV: invoice.invoiceMVOriginal ?? 0,
        };
        const calculatedOriginals = invoice.includeOtherParcels
            ? this._calculateIncludeOtherParcelValues(this._otherParcelResetValues)
            : this._otherParcelResetValues;
        const siteChangeTAVOriginal = Number(calculatedOriginals.previousTAV) - Number(calculatedOriginals.invoiceTAV);
        const siteChangeMVOriginal = calculatedOriginals.previousMV - calculatedOriginals.invoiceMV;
        const feeAmount = this.calculateFeeAmount(
            invoice.contingencyPctOriginal ?? 0,
            invoice.taxSavingsTotalOriginal ?? 0,
            invoice.contingencyCapOriginal,
        );

        this._originalInvoiceValues = {
            previousTAV: this._hasAnyTaxableAssessedValue
                ? (calculatedOriginals.previousTAV ?? invoice.previousTAVOriginal ?? 0)
                : null,
            previousMV: calculatedOriginals.previousMV ?? invoice.previousMVOriginal ?? 0,
            invoiceTAV: this._hasAnyTaxableAssessedValue
                ? (calculatedOriginals.invoiceTAV ?? invoice.invoiceTAVOriginal ?? 0)
                : null,
            invoiceMV: calculatedOriginals.invoiceMV ?? invoice.invoiceMVOriginal ?? 0,
            siteChangeTAV: this._hasAnyTaxableAssessedValue ? siteChangeTAVOriginal : null,
            siteChangeMV: siteChangeMVOriginal,
            billableTaxRate: invoice.billableTaxRateOriginal ?? 0,
            taxSavingsTotal: invoice.taxSavingsTotalOriginal ?? 0,
            contingencyPct: invoice.contingencyPctOriginal,
            contingencyCap: invoice.contingencyCapOriginal,
            feeAmount: feeAmount,
            fixedFee: invoice.fixedFeeOriginal,
            expenseAmount: invoice.expenseAmount ?? 0,
            invoiceAmount: feeAmount + invoice.fixedFeeOriginal + invoice.expenseAmount,
            includeOtherParcels: invoice.includeOtherParcels,
        };

        // Tax Savings Summary
        invoiceValues.previousMV = invoice.previousMV || invoice.previousMVOriginal || 0;
        invoiceValues.invoiceMV = invoice.invoiceMV || invoice.invoiceMVOriginal || 0;
        invoiceValues.previousTAV = this._hasAnyTaxableAssessedValue
            ? invoice.previousTAV || invoice.previousTAVOriginal || 0
            : null;
        invoiceValues.invoiceTAV = this._hasAnyTaxableAssessedValue
            ? invoice.invoiceTAV || invoice.invoiceTAVOriginal || 0
            : null;
        invoiceValues.billableTaxRate = invoice.billableTaxRate || 0;
        invoiceValues.taxSavingsTotal = invoice.taxSavingsTotal || 0;

        //Invoice Summary
        invoiceValues.contingencyPct = invoice.contingencyPct;
        invoiceValues.contingencyCap = invoice.contingencyCap;
        invoiceValues.feeAmount = invoice.feeAmount;
        invoiceValues.fixedFee = invoice.fixedFee;
        invoiceValues.expenseAmount = invoice.expenseAmount ?? 0;
        invoiceValues.invoiceAmount = invoice.invoiceAmount;

        if (invoice.includeOtherParcels) {
            invoiceValues.previousMV = invoiceValues.previousMV + (invoice.otherParcelMV || 0);
            invoiceValues.invoiceMV = invoiceValues.invoiceMV + (invoice.otherParcelMV || 0);

            invoiceValues.previousTAV = this._hasAnyTaxableAssessedValue
                ? invoiceValues.previousTAV + (invoice.otherParcelTaxableAV || 0)
                : null;
            invoiceValues.invoiceTAV = this._hasAnyTaxableAssessedValue
                ? invoiceValues.invoiceTAV + (invoice.otherParcelTaxableAV || 0)
                : null;
        }

        const originals = {
            previousTAV: invoice.previousTAV,
            previousMV: invoice.previousMV,
            invoiceTAV: invoice.invoiceTAV,
            invoiceMV: invoice.invoiceMV,
        };
        const calculatedValues = invoice.includeOtherParcels
            ? this._calculateIncludeOtherParcelValues(originals)
            : originals;
        const siteChangeTAV = Number(calculatedValues.previousTAV) - Number(calculatedValues.invoiceTAV);
        const siteChangeMV = calculatedValues.previousMV - calculatedValues.invoiceMV;

        const formattedTotals: FormattedInvoiceDetails = {
            ...invoiceValues,
            siteChangeTAV: this._hasAnyTaxableAssessedValue ? siteChangeTAV : null,
            siteChangeMV,
        };

        if (this._hasMultipleTaxableValues) {
            formattedTotals.previousTAVValue = <number>formattedTotals.previousTAV;
            formattedTotals.invoiceTAVValue = <number>formattedTotals.invoiceTAV;
        }

        this._invoiceResetValues = {
            ...formattedTotals,
            includeOtherParcels: invoice.includeOtherParcels,
        };

        return formattedTotals;
    }

    generateNotes(invoice: Invoice): void {
        if (!invoice.automaticallyUpdateNotes) {
            return;
        }

        let tempNotes: string = '';

        const litigationPostfix =
            invoice.contractServiceType === ContractServiceType.Litigation ||
            invoice.contractServiceType === ContractServiceType.LitigationAdmin ||
            invoice.contractServiceType === ContractServiceType.LitigationOnly
                ? ' - Litigation'
                : '';
        tempNotes += `${invoice.annualYear} Property Tax Services${litigationPostfix}\n`;
        if (invoice.siteName) {
            tempNotes += `${invoice.siteName}\n`;
        }

        if (invoice.siteAddress) {
            if (invoice.siteAddress.address1) {
                tempNotes += `${invoice.siteAddress.address1}\n`;
            }
            if (invoice.siteAddress.address2) {
                tempNotes += `${invoice.siteAddress.address2}\n`;
            }
            if (invoice.siteAddress.city) {
                tempNotes += invoice.siteAddress.city;
                if (invoice.siteState || invoice.siteAddress.zip) {
                    tempNotes += ', ';
                } else {
                    tempNotes += '\n';
                }
            }
            if (invoice.siteState.abbr) {
                tempNotes += invoice.siteState.abbr;
                if (invoice.siteAddress.zip) {
                    tempNotes += ' ';
                } else {
                    tempNotes += '\n';
                }
            }
            if (invoice.siteAddress.zip) {
                tempNotes += `${invoice.siteAddress.zip}\n`;
            }
        }

        if (
            (invoice.contractServiceType === ContractServiceType.Litigation ||
                invoice.contractServiceType === ContractServiceType.LitigationOnly) &&
            !invoice.isAdHoc
        ) {
            tempNotes += `Case #: ${invoice.appeals[0].caseNumber || ''}\n`;
        }

        tempNotes += '\n';

        if (!invoice.isAdHoc) {
            const hasAnyTaxableValue =
                invoice.taxSavings?.some((x) => x.hasAnyTaxableAssessedValue) &&
                invoice.taxSavings?.some((x) => x.hasAnyRate) &&
                !this._hasMultipleTaxableValues;

            const previousLabel = invoice.includeOtherParcels ? 'Site Previous MV' : 'Parcel(s) Previous MV';
            const billableLabel = invoice.includeOtherParcels ? 'Site Billable MV' : 'Parcel(s) Billing MV';

            tempNotes += `${previousLabel}: ${this._numberPipe.transform(invoice.previousMV || 0, '1.0-0')}`;
            tempNotes +=
                hasAnyTaxableValue ||
                (invoice.previousTAV && invoice.previousTAV !== this._originalInvoiceValues?.previousTAV)
                    ? ` | Taxable Assessed Value: ${this._numberPipe.transform(invoice.previousTAV || 0, '1.0-0')}\n`
                    : '\n';
            tempNotes += `${billableLabel}: ${this._numberPipe.transform(invoice.invoiceMV || 0, '1.0-0')}`;
            tempNotes +=
                hasAnyTaxableValue ||
                (invoice.invoiceTAV && invoice.invoiceTAV !== this._originalInvoiceValues?.invoiceTAV)
                    ? ` | Taxable Assessed Value: ${this._numberPipe.transform(invoice.invoiceTAV || 0, '1.0-0')}\n`
                    : '\n';
        }

        if (invoice.billableTaxRate) {
            tempNotes += `Billable Rate: ${this._percentPipe.transform(invoice.billableTaxRate, '1.6-6')}\n`;
        }
        if (!invoice.isAdHoc) {
            const val =
                !this._hasMultipleTaxableValues ||
                invoice.taxSavingsTotal !== this._originalInvoiceValues?.taxSavingsTotal
                    ? invoice.taxSavingsTotal
                    : invoice.taxSavingsTotalOriginal;
            tempNotes += `Tax Savings: ${this._currencyPipe.transform(val || 0, 'USD', 'symbol')}`;
            tempNotes += this._hasMultipleTaxableValues || invoice.includeTaxSavingsWorksheet ? '*\n' : `\n`;
        }
        if (invoice.feeAmount) {
            tempNotes += `Contingency: ${this._percentPipe.transform(invoice.contingencyPct, '1.2-2')}\n`;
            tempNotes += `${invoice.contingencyCap && invoice.contingencyCap < invoice.feeAmount ? 'Uncapped ' : ''}Contingency Fee Amount: ${this._currencyPipe.transform(invoice.feeAmount, 'USD', 'symbol')}\n`;
            tempNotes +=
                invoice.contingencyCap && invoice.contingencyCap < invoice.feeAmount
                    ? `Cap on Fees: ${this._currencyPipe.transform(invoice.contingencyCap, 'USD', 'symbol')}\n`
                    : '';
        }
        if (invoice.fixedFee) {
            let fixedFeeLabel: string;

            if (invoice.isRyanLegalServices) {
                fixedFeeLabel = `Ryan Legal Services (RLS) - Fixed Fee: `;
            } else {
                const contractServiceType = Object.keys(this._constants.ContractServiceTypes)
                    .map((key) => this._constants.ContractServiceTypes[key])
                    .filter((t) => t.id == invoice.contractServiceType)[0];

                fixedFeeLabel = `${contractServiceType.fullDisplayName || contractServiceType.name} Fixed Fee: `;
            }
            tempNotes += `${fixedFeeLabel + this._currencyPipe.transform(invoice.fixedFee, 'USD', 'symbol')}\n`;
        }
        tempNotes +=
            invoice.expenseAmount && invoice.expenseAmount != 0
                ? `Expenses: ${this._currencyPipe.transform(invoice.expenseAmount, 'USD', 'symbol')}\n`
                : '';

        if (invoice.feeAmount != invoice.invoiceAmount) {
            tempNotes += `Invoice Amount: ${this._currencyPipe.transform(invoice.invoiceAmount, 'USD', 'symbol')}\n`;
        }

        tempNotes += `\nInternal ID: ${invoice.invoiceId.toString()}`;

        if (this._hasMultipleTaxableValues || invoice.includeTaxSavingsWorksheet) {
            tempNotes += `\n\n*See tax savings worksheet for savings calculation details`;
        }

        this._notesSubject.next(tempNotes);
    }

    calculateFeeAmount(contingencyPct: number, taxSavings: number, contingencyCap: number): number {
        if (!contingencyPct) {
            return 0;
        }

        const totalSavingsDec: Decimal = new Decimal(taxSavings);
        let calcFee: number = totalSavingsDec.times(contingencyPct).toNumber();

        if (contingencyCap !== undefined && contingencyCap !== null && calcFee > contingencyCap) {
            calcFee = contingencyCap;
        }

        return new Decimal(calcFee).toDecimalPlaces(2).toNumber();
    }

    updateIncludeOtherParcel(includeOtherParcels: boolean, currentValues: IncludeParcelsReset): IncludeParcelsReset {
        const original = includeOtherParcels
            ? this._calculateIncludeOtherParcelValues(this._otherParcelResetValues)
            : this._otherParcelResetValues;
        const calculated = includeOtherParcels
            ? this._calculateIncludeOtherParcelValues(currentValues)
            : this._removeIncludeOtherParcelValues(currentValues);

        this._originalInvoiceValues.previousTAV = original.previousTAV;
        this._originalInvoiceValues.previousMV = original.previousMV;
        this._originalInvoiceValues.invoiceTAV = original.invoiceTAV;
        this._originalInvoiceValues.invoiceMV = original.invoiceMV;

        return {
            previousTAV:
                this._overrides.previousTAV === OverrideStatus.Manual
                    ? currentValues.previousTAV
                    : calculated.previousTAV,
            previousMV:
                this._overrides.previousMV === OverrideStatus.Manual ? currentValues.previousMV : calculated.previousMV,
            invoiceTAV:
                this._overrides.invoiceTAV === OverrideStatus.Manual ? currentValues.invoiceTAV : calculated.invoiceTAV,
            invoiceMV:
                this._overrides.invoiceMV === OverrideStatus.Manual ? currentValues.invoiceMV : calculated.invoiceMV,
        };
    }

    private _calculateIncludeOtherParcelValues(currentValues: IncludeParcelsReset): IncludeParcelsReset {
        const previousTAV = Number(currentValues.previousTAV) + (this._currentInvoice.otherParcelTaxableAV || 0);
        const invoiceTAV = Number(currentValues.invoiceTAV) + (this._currentInvoice.otherParcelTaxableAV || 0);
        return {
            previousTAV: this._hasAnyTaxableAssessedValue ? previousTAV : null,
            previousMV: currentValues.previousMV + (this._currentInvoice.otherParcelMV || 0),
            invoiceTAV: this._hasAnyTaxableAssessedValue ? invoiceTAV : null,
            invoiceMV: currentValues.invoiceMV + (this._currentInvoice.otherParcelMV || 0),
        };
    }

    private _removeIncludeOtherParcelValues(currentValues: IncludeParcelsReset): IncludeParcelsReset {
        const previousTAV = Number(currentValues.previousTAV) - (this._currentInvoice.otherParcelTaxableAV || 0);
        const invoiceTAV = Number(currentValues.invoiceTAV) - (this._currentInvoice.otherParcelTaxableAV || 0);
        return {
            previousTAV: this._hasAnyTaxableAssessedValue ? previousTAV : null,
            previousMV: currentValues.previousMV - (this._currentInvoice.otherParcelMV || 0),
            invoiceTAV: this._hasAnyTaxableAssessedValue ? invoiceTAV : null,
            invoiceMV: currentValues.invoiceMV - (this._currentInvoice.otherParcelMV || 0),
        };
    }
}
